import "@/App.css";
import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./AuthContext";
import { LanguageProvider } from "./i18n";
import { Toaster } from "./components/ui/sonner";
import Landing from "./pages/Landing";
import Auth from "./pages/Auth";
import Onboarding from "./pages/Onboarding";
import Dashboard from "./pages/Dashboard";
import TrackDay from "./pages/TrackDay";
import Diary from "./pages/Diary";
import Program from "./pages/Program";
import Summary from "./pages/Summary";
import Reports from "./pages/Reports";
// eslint-disable-next-line no-unused-vars
import Devices from "./pages/Devices";
import AdminDashboard from "./pages/AdminDashboard";
import Profile from "./pages/Profile";
import Protocols from "./pages/Protocols";
import Learn from "./pages/Learn";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import Legal from "./pages/Legal";
import About from "./pages/About";
import Contact from "./pages/Contact";
import TenantOnboarding from "./pages/TenantOnboarding";

const Loader = () => (
  <div className="min-h-screen flex items-center justify-center bg-[#FDFBF7]">
    <div className="w-10 h-10 rounded-full border-2 border-[#3F6E85] border-t-transparent animate-spin" />
  </div>
);

function Protected({ children, adminOnly, onboarding }) {
  const { user, loading } = useAuth();
  if (loading) return <Loader />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === "admin") return adminOnly ? children : <Navigate to="/admin" replace />;
  if (adminOnly) return <Navigate to="/dashboard" replace />;
  if (onboarding) return user.onboarded ? <Navigate to="/dashboard" replace /> : children;
  if (!user.onboarded) return <Navigate to="/onboarding" replace />;
  return children;
}

function PublicOnly({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <Loader />;
  if (user) return <Navigate to={user.role === "admin" ? "/admin" : "/dashboard"} replace />;
  return children;
}

function App() {
  return (
    <LanguageProvider>
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/terms" element={<Legal doc="terms" />} />
            <Route path="/privacy" element={<Legal doc="privacy" />} />
            <Route path="/about" element={<About />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/login" element={<PublicOnly><Auth mode="login" /></PublicOnly>} />
            <Route path="/register" element={<PublicOnly><Auth mode="register" /></PublicOnly>} />
            <Route path="/forgot-password" element={<PublicOnly><ForgotPassword /></PublicOnly>} />
            <Route path="/reset-password" element={<PublicOnly><ResetPassword /></PublicOnly>} />
            <Route path="/onboarding" element={<Protected onboarding><Onboarding /></Protected>} />
            <Route path="/dashboard" element={<Protected><Dashboard /></Protected>} />
            <Route path="/track" element={<Protected><TrackDay /></Protected>} />
            <Route path="/diary" element={<Protected><Diary /></Protected>} />
            <Route path="/program" element={<Protected><Program /></Protected>} />
            <Route path="/summary" element={<Protected><Summary /></Protected>} />
            <Route path="/reports" element={<Protected><Reports /></Protected>} />
            <Route path="/devices" element={<Navigate to="/dashboard" replace />} />
            <Route path="/profile" element={<Protected><Profile /></Protected>} />
            <Route path="/protocols" element={<Protected><Protocols /></Protected>} />
            <Route path="/learn/:pillar" element={<Protected><Learn /></Protected>} />
            <Route path="/admin" element={<Protected adminOnly><AdminDashboard /></Protected>} />
            <Route path="/:slug" element={<TenantOnboarding />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <Toaster position="top-center" richColors />
        </BrowserRouter>
      </AuthProvider>
    </LanguageProvider>
  );
}

export default App;
